import { t } from "i18next";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TitleLine from "../../../components/Assets/TitleLine/TitleLine";
import FormAccomplishments from "../../../components/Form/FormAccomplishments/FormAccomplishments";
import FormApprovalCompleteTop from "../../../components/Form/FormApproval/FormApprovalCompleteTop";
import FormChoosePermitsComplete from "../../../components/Form/FormChoosePermits/FormChoosePermitsComplete";
import FormEducationComplete from "../../../components/Form/FormEducation/FormEducationComplete";
import FormTitle from "../../../components/Form/FormTitle/FormTitle";
import { useCurrentDataContext } from "../../../contexts/CurrentDataContext";
import { useRoutesContext } from "../../../contexts/RoutesContext";
import { useUserContext } from "../../../contexts/UserContext";
import { approveForm, getForm } from "../../../scripts/dataHandlers";
import classes from "./DoctorApprovalPage.module.css";
import axios from "axios";
import ReactLoading from "react-loading";
import { toast } from "react-toastify";
import FormEmployeeNameAndBirth from "../../../components/Form/FormEmployeeNameAndBirth/FormEmployeeNameAndBirth";
import { convertDate } from "../../../utils/convertDate";
import DoctorAttachedAccomplishments from "../../../components/Doctor/DoctorAttachedAccomplishments/DoctorAttachedAccomplishments";
import FormUnitAndJobTitle from "../../../components/Form/FormUnitAndJobTitle/FormUnitAndJobTitle";
import { useRequiredAccomplishmentsContext } from "../../../contexts/RequiredAccomplishmentsContext";
import { useErrorContext } from "../../../contexts/ErrorContext";
import { PermitFormBody } from "../../../contexts/interfaces";
import { requiredVaccination } from "../../../utils/requiredVaccination";
import DoctorReturnConfirm from "../../../components/Doctor/DoctorReturnConfirm/DoctorReturnConfirm";
import { validateRequiredAccomplishments } from "../../../utils/accomplishmentDateRules";

const DoctorApprovalPage = () => {
  const { empId, formId } = useParams();

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [isHandling, setIsHandling] = useState<boolean>(false);

  const [confirmNeeded, setConfirmNeeded] = useState<boolean>(false);
  const [approveDisabled, setApproveDisabled] = useState<boolean>(true);
  const [accomplishmentBlockReason, setAccomplishmentBlockReason] = useState<
    string | null
  >(null);

  const { currentData, setCurrentData } = useCurrentDataContext();
  const { user } = useUserContext();
  const { requiredAccomplishments, setRequiredAccomplishments } =
    useRequiredAccomplishmentsContext();

  const navigate = useNavigate();

  const { routes } = useRoutesContext();
  const { handleError } = useErrorContext();

  useEffect(() => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    const getFormData = async () => {
      // If user and applicationId is available, use them to get formData
      const res =
        user &&
        formId &&
        (await getForm(
          user.id,
          Number(empId),
          "waiting",
          Number(formId),
          source
        ));

      // If response status is ok, set res to currentData context
      if (res?.status === 200) {
        setCurrentData({ ...res.data.result[0] });
        // If vaccination is checked from form (permitItemId = 8), add Vaccination in required permits
        requiredAccomplishments &&
          res.data.result[0].permits.find(
            (p: PermitFormBody) => p.permitItemId === 8
          )?.isChecked === true &&
          res.data.result[0].permits.find(
            (p: PermitFormBody) => p.permitItemId === 8
          ) &&
          requiredAccomplishments.findIndex(
            (p) => p.type === requiredVaccination.type
          ) === -1 &&
          setRequiredAccomplishments([
            ...requiredAccomplishments,
            requiredVaccination,
          ]);
        setIsLoading(false);
      } else {
        handleError(res, "GetFormFailed");
      }
    };

    user?.isDoctor && getFormData();

    return () => source.cancel();
  }, [
    formId,
    user,
    empId,
    handleError,
    requiredAccomplishments,
    setCurrentData,
    setRequiredAccomplishments,
  ]);

  useEffect(() => {
    if (!requiredAccomplishments || !currentData?.accomplishments) {
      setApproveDisabled(true);
      return;
    }

    const result = validateRequiredAccomplishments(
      requiredAccomplishments,
      currentData.accomplishments
    );

    if (!result.valid) {
      setApproveDisabled(true);
      if (result.expired.length > 0) {
        setAccomplishmentBlockReason(
          String(t("doctorApproval.cannotApproveExpiredAccomplishments"))
        );
      } else {
        setAccomplishmentBlockReason(
          String(t("doctorApproval.cannotApproveMissingAccomplishments"))
        );
      }
      return;
    }

    setAccomplishmentBlockReason(null);
    setApproveDisabled(false);
  }, [requiredAccomplishments, currentData?.accomplishments, t]);

  const handleApprove = () => {
    if (approveDisabled) {
      return;
    }
    setIsHandling(true);
    toast(`${t("message.handlingForm")}`, {
      toastId: "handlingForm",
      position: "top-center",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      type: "default",
    });

    // Approve form in backend, switching formStatus to approved
    const doApprove = async () => {
      const res =
        user &&
        currentData?.formId &&
        currentData?.employeeId &&
        currentData?.replacePrevious !== undefined &&
        (await approveForm(
          user.id,
          currentData.formId,
          currentData.employeeId,
          currentData.replacePrevious,
          user.firstName,
          user.lastName,
          user.doctorUniqueIdCode
        ));

      setIsHandling(false);
      toast.dismiss("handlingForm");

      if (res?.status === 200) {
        // Success! -message about returning, after that navigate
        toast(`${t("message.formApprovedSuccessfully")}`, {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          type: "success",
        });
        setCurrentData(undefined);
        navigate(`/${routes.doctorMain}`);
      } else if (res?.data === "AccomplishmentTooOld") {
        handleError(res, "AccomplishmentTooOld");
      } else if (res?.data === "AccomplishmentMissing") {
        handleError(res, "AccomplishmentMissing");
      } else {
        handleError(res, "ApproveFormFailed");
      }
    };
    user && currentData && doApprove();
  };

  const handleReturnToMain = () => {
    setCurrentData(undefined);
    navigate(`/${routes.doctorMain}`);
  };

  return user && currentData?.formId ? (
    <>
      <div className={classes.pageContainer}>
        <div className={classes.innerContainer}>
          <FormTitle />
          <TitleLine />

          <div className={classes.container}>
            <div className={classes.topRow}>
              {`${t("formChosenEmployee.applicant")}`}
            </div>
            <FormEmployeeNameAndBirth
              firstName={currentData.firstName}
              lastName={currentData.lastName}
              dateOfBirth={convertDate(currentData.dateOfBirth || "")}
            />

            {currentData.unitName && currentData.jobTitle && (
              <FormUnitAndJobTitle
                unitName={currentData.unitName}
                jobTitle={currentData.jobTitle}
              />
            )}

            <FormAccomplishments
              employeeAccomplishments={currentData.accomplishments}
              showOnlyRequired={false}
              showOrigin={true}
              checkFormData={true}
            />

            {
              // If any of accomplishments are verified by attachment instead of skhole data,
              // show section for names and attachment file
              currentData.accomplishments.some(
                (accomplishment) => accomplishment.fromAttachment
              ) && (
                <DoctorAttachedAccomplishments
                  accomplishments={currentData.accomplishments}
                  attachments={
                    currentData.records?.filter(
                      (record) => record.type === "accomplishment"
                    ) || []
                  }
                />
              )
            }
          </div>

          <FormEducationComplete
            formSubmitHandler={undefined}
            setLoading={undefined}
          />

          <FormChoosePermitsComplete formSubmitHandler={undefined} />

          <div className={classes.container}>
            <FormApprovalCompleteTop />
          </div>
        </div>
      </div>
      {accomplishmentBlockReason && (
        <div className={classes.blockReason}>{accomplishmentBlockReason}</div>
      )}

      <div
        className={classes.buttonsContainer}
        style={{ opacity: `${isHandling ? "0.3" : "1"}` }}
      >
        <button
          className={classes.approveButton}
          onClick={() => handleApprove()}
          disabled={isHandling || approveDisabled}
        >
          {`${t("doctorApproval.approveButton")}`}
        </button>
        <button
          className={classes.returnButton}
          onClick={() => setConfirmNeeded(true)}
          disabled={isHandling}
        >
          {`${t("doctorApproval.returnButton")}`}
        </button>
        <button
          className={classes.returnButton}
          onClick={() => handleReturnToMain()}
          disabled={isHandling}
        >
          {`${t("doctorApproval.returnBackToMainButton")}`}
        </button>
      </div>

      {confirmNeeded && (
        <DoctorReturnConfirm
          setConfirmNeeded={setConfirmNeeded}
          setIsHandling={setIsHandling}
        />
      )}
    </>
  ) : isLoading ? (
    <div className={classes.loadingContainer}>
      <ReactLoading
        type={"spokes"}
        color={"#ED1B24"}
        height={"50px"}
        width={"50px"}
      />

      <div className={classes.loadingText}>{`${t("mainPage.loading")}`}</div>
    </div>
  ) : (
    <div className={classes.notFoundContainer}>
      {`${t("doctorApproval.notFound")}`}
    </div>
  );
};

export default DoctorApprovalPage;
